/** @format */

import { apiSlice } from "../apiSlice";
import { BLResource } from "../../features/resources/BLResourceTypes";
import {
  ResourceReference,
  QualificationId,
  Card,
  Lesson,
  Resource,
  ResourceObject,
  Sublesson,
} from "../../types";
import { Locale } from "../../i18n/init";
import { store } from "../store";
import { DeepPartial } from "@reduxjs/toolkit";

export const fetchSubjectFile = (subjectFile: string) => {
  return import(`../../json/${subjectFile}`);
};

interface ResourceType {
  created: string;
  SK: "RESOURCE";
  Status: "live" | "draft";
  UUID: string;
  object: BLResource;
  PK: string;
  modified: string;
}

type FullSublesson = Omit<Sublesson, "cards"> & { cards: Card[] };
type FullLesson = Omit<Lesson, "subLessons"> & { subLessons: FullSublesson[] };
type FullResourceObject = Omit<ResourceObject, "lessons"> & {
  lessons: FullLesson[];
};
export type FullResource = Omit<Resource, "Object"> & {
  Object: FullResourceObject;
};

/** API Endpoints related to Resources and Resource references */
export const resourceApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /**
     * createResource mutation (POST)
     * @param {BLResource} resource - the resource item to be created.
     * @returns {BLResource} the resource just created.
     */
    createResource: build.mutation<BLResource, { resource: BLResource }>({
      query(body) {
        return {
          url: "resource/create-resource",
          method: "POST",
          body: { ...body },
        };
      },
      invalidatesTags: [{ type: "ResourceRef", id: "LIST" }],
    }),

    /**
     * getResource query (GET)
     * @param {string} code - the code of the resource.
     * @returns {ResourceType} the fetched resource.
     */
    getResource: build.query<ResourceType, string>({
      query: (code) => {
        if (store.getState().auth.userToken?.token) {
          return `resource/auth/${code}`;
        }

        return `resource/${code}`;
      },
    }),

    /**
     * getResourceReference query (GET)
     * @param {string} code - the code of the resource.
     * @param {Locale} locale - the locale of the resource.
     * @param {string} type - the type of resource.
     * @param {string} subjectId - the resource's subject.
     * @param {QualificationId} qualification - the resource's qualification.
     * @returns {ResourceReference} the fetched resource reference.
     */
    getResourceReference: build.query<
      ResourceReference,
      {
        code: string;
        locale: Locale;
        type: string;
        subjectId: string;
        qualification: QualificationId;
      }
    >({
      query: (args) => {
        const { code, locale, type, subjectId, qualification } = args;

        // If logged in, send request to auth endpoint
        if (store.getState().auth.userToken?.token) {
          return `resource-ref/auth/${code}/${locale}?type=${type}&subject=${subjectId}&qualification=${qualification}`;
        }
        return `resource-ref/${code}/${locale}?type=${type}&subject=${subjectId}&qualification=${qualification}`;
      },
      transformResponse: (response: { resourceRef: ResourceReference }) =>
        response.resourceRef,
      providesTags: (result) => [
        { type: "ResourceRef" as const, id: result?.UUID },
      ],
    }),

    /**
     * getFileResource query (GET)
     * @param {string} code - the code of the resource.
     * @returns {BLResource} the fetched resource.
     */
    getFileResource: build.query<BLResource, string>({
      query: (code) => `file-resource/${code}`,
    }),

    /**
     * updateResourceReference mutation (POST)
     * @param {Partial<ResourceReference>} resourceReferenceItem - the resource reference.
     * @returns {ResourceReference} the fetched resource.
     */
    updateResourceReference: build.mutation<
      ResourceReference,
      {
        primaryKey: { PK: string; SK: string };
        refInformation: { locale: Locale; type: string; code: string };
        changes: DeepPartial<ResourceReference>;
      }
    >({
      query: (body) => {
        return {
          url: `resource-ref/save`,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: (result) => [
        { type: "ResourceRef" as const, id: result?.UUID },
        { type: "ResourceRef", id: "LIST" },
      ],
      transformResponse: (response: { resourceRef: ResourceReference }) =>
        response.resourceRef,
    }),
    createResourceReference: build.mutation<
      ResourceReference,
      { resourceReferenceItem: ResourceReference }
    >({
      query(body) {
        return {
          url: "resource-ref/create",
          method: "POST",
          body: { ...body },
        };
      },
      invalidatesTags: (result) => [
        { type: "ResourceRef" as const, id: result?.UUID },
        { type: "ResourceRef", id: "LIST" },
      ],
    }),

    /**
     * deleteResource query (DELETE)
     * @param {string} code - the code of the resource.
     * @returns {string, string} a success message and the objects deleted in JSON.stringify form
     */
    deleteResource: build.mutation<
      { message: string; deletedValues: string },
      string
    >({
      query: (code) => {
        return {
          url: `/resource-delete/${code}`,
          method: "DELETE",
        };
      },
      invalidatesTags: () => [
        { type: "ResourceRef", id: "LIST" },
      ],
    }),

    /**
     * getResourceClones query (GET)
     * @param {string} code - the code of the resource.
     * @returns { {}[], {}[] } an array of keys for each locales clones
     */
    getResourceClones: build.query<
      {
        enCopies: { PK: string; SK: string }[];
        cyCopies: { PK: string; SK: string }[];
      },
      string
    >({
      query: (code) => `/resource/clones/${code}`,
    }),

    getFullResource: build.query<FullResource, { code: string }>({
      query: ({ code }) => {
        return `resource/auth/getFull/${code}`;
      },
      transformResponse: (response: { fullResource: FullResource }) =>
        response.fullResource,
    }),
  }),
});

export const {
  useCreateResourceMutation,
  useGetResourceQuery,
  useGetResourceReferenceQuery,
  useGetFileResourceQuery,
  useUpdateResourceReferenceMutation,
  useCreateResourceReferenceMutation,
  useDeleteResourceMutation,
  useGetResourceClonesQuery,
  useGetFullResourceQuery,
} = resourceApiSlice;
